/**
 * Google Analytics Service for Notea Platform
 * Handles async loading, page tracking, and custom events
 * GDPR-compliant with consent mode support
 */

// Types for gtag
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

interface ConsentState {
  analytics: boolean;
  marketing: boolean;
  version: string;
  timestamp: string;
}

interface EventParams {
  [key: string]: string | number | boolean | undefined;
}

const CONSENT_KEY = 'notea_cookie_consent';
const CONSENT_VERSION = '1.0';

class AnalyticsService {
  private initialized = false;
  private measurementId: string | null = null;
  private loadPromise: Promise<void> | null = null;
  private tenantId: number | null = null;
  private environment: string = process.env.REACT_APP_ENV || 'unknown';

  /**
   * Get common parameters to include with all events
   */
  private getCommonParams(): EventParams {
    return {
      tenant_id: this.tenantId ?? undefined,
      environment: this.environment,
    };
  }

  /**
   * Initialize Google Analytics asynchronously
   * @param measurementId - GA4 Measurement ID (e.g., G-XXXXXXXXXX)
   */
  async initialize(measurementId?: string): Promise<void> {
    // Get measurement ID from param or environment
    this.measurementId = measurementId || process.env.REACT_APP_GA_MEASUREMENT_ID || null;

    if (!this.measurementId) {
      console.warn('[Analytics] No GA Measurement ID provided. Analytics disabled.');
      return;
    }

    if (this.initialized) {
      return;
    }

    // Return existing load promise if already loading
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = this._loadGtag();
    await this.loadPromise;
    this.initialized = true;
  }

  /**
   * Load gtag.js script asynchronously
   */
  private async _loadGtag(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Initialize dataLayer
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag(...args) {
        window.dataLayer.push(args);
      };

      // Set default consent BEFORE loading the script (required for Consent Mode v2)
      const storedConsent = this._getStoredConsent();
      window.gtag('consent', 'default', {
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        ad_storage: 'denied',
        analytics_storage: storedConsent?.analytics ? 'granted' : 'denied',
        wait_for_update: 500,
      });

      // Create and load the script
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
      script.async = true;

      script.onload = () => {
        // Configure gtag
        window.gtag('js', new Date());
        window.gtag('config', this.measurementId!, {
          anonymize_ip: true,
          allow_google_signals: false,
          allow_ad_personalization_signals: false,
          send_page_view: false, // We'll handle page views manually for SPA
        });

        resolve();
      };

      script.onerror = () => {
        console.error('[Analytics] Failed to load Google Analytics');
        reject(new Error('Failed to load gtag.js'));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Get stored consent from localStorage
   */
  private _getStoredConsent(): ConsentState | null {
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (stored) {
        const consent = JSON.parse(stored) as ConsentState;
        if (consent.version === CONSENT_VERSION) {
          return consent;
        }
      }
    } catch (e) {
      console.warn('[Analytics] Error reading consent:', e);
    }
    return null;
  }

  /**
   * Check if analytics consent is granted
   */
  hasAnalyticsConsent(): boolean {
    const consent = this._getStoredConsent();
    return consent?.analytics ?? false;
  }

  /**
   * Set the current tenant ID for tracking
   */
  setTenantId(tenantId: number | null): void {
    this.tenantId = tenantId;

    // Also set as user property for GA
    if (this.initialized && tenantId) {
      this.setUserProperties({ tenant_id: tenantId });
    }
  }

  /**
   * Get the current tenant ID
   */
  getTenantId(): number | null {
    return this.tenantId;
  }

  /**
   * Update consent state
   */
  updateConsent(analytics: boolean, marketing: boolean = false): void {
    // Store consent
    const consent: ConsentState = {
      version: CONSENT_VERSION,
      analytics,
      marketing,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));

    // Update gtag consent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: analytics ? 'granted' : 'denied',
        ad_storage: marketing ? 'granted' : 'denied',
        ad_user_data: marketing ? 'granted' : 'denied',
        ad_personalization: marketing ? 'granted' : 'denied',
      });
    }

    // Track consent event
    if (analytics) {
      this.trackEvent('consent_granted', {
        analytics: analytics ? 'true' : 'false',
        marketing: marketing ? 'true' : 'false',
      });
    }
  }

  /**
   * Track a page view (for SPA navigation)
   */
  trackPageView(path: string, title?: string): void {
    if (!this.initialized || !this.hasAnalyticsConsent()) {
      return;
    }

    window.gtag?.('event', 'page_view', {
      ...this.getCommonParams(),
      page_path: path,
      page_title: title || document.title,
      page_location: window.location.origin + path,
    });
  }

  /**
   * Track a custom event
   */
  trackEvent(eventName: string, params?: EventParams): void {
    if (!this.initialized || !this.hasAnalyticsConsent()) {
      return;
    }

    window.gtag?.('event', eventName, {
      ...this.getCommonParams(),
      ...params,
    });
  }

  /**
   * Track a modal open event
   */
  trackModalOpen(modalName: string, params?: EventParams): void {
    this.trackEvent('modal_open', {
      modal_name: modalName,
      ...params,
    });
  }

  /**
   * Track a modal close event
   */
  trackModalClose(modalName: string, params?: EventParams): void {
    this.trackEvent('modal_close', {
      modal_name: modalName,
      ...params,
    });
  }

  /**
   * Track a button click
   */
  trackButtonClick(buttonName: string, params?: EventParams): void {
    this.trackEvent('button_click', {
      button_name: buttonName,
      ...params,
    });
  }

  /**
   * Track a form submission
   */
  trackFormSubmit(formName: string, success: boolean, params?: EventParams): void {
    this.trackEvent('form_submit', {
      form_name: formName,
      success: success ? 'true' : 'false',
      ...params,
    });
  }

  /**
   * Track feature usage
   */
  trackFeatureUsage(featureName: string, action: string, params?: EventParams): void {
    this.trackEvent('feature_usage', {
      feature_name: featureName,
      action,
      ...params,
    });
  }

  /**
   * Track AI automation events
   */
  trackAIAutomation(
    action: 'enabled' | 'disabled' | 'search_started' | 'nego_started' | 'result_found' | 'booking_confirmed',
    params?: EventParams
  ): void {
    this.trackEvent('ai_automation', {
      action,
      ...params,
    });
  }

  /**
   * Track errors
   */
  trackError(errorType: string, errorMessage: string, params?: EventParams): void {
    this.trackEvent('error', {
      error_type: errorType,
      error_message: errorMessage,
      ...params,
    });
  }

  /**
   * Track timing (performance)
   */
  trackTiming(category: string, variable: string, valueMs: number, params?: EventParams): void {
    this.trackEvent('timing_complete', {
      event_category: category,
      name: variable,
      value: Math.round(valueMs),
      ...params,
    });
  }

  /**
   * Set user properties
   */
  setUserProperties(properties: Record<string, string | number | boolean>): void {
    if (!this.initialized) {
      return;
    }

    window.gtag?.('set', 'user_properties', properties);
  }

  /**
   * Set user ID (for cross-device tracking, if consented)
   */
  setUserId(userId: string | null): void {
    if (!this.initialized) {
      return;
    }

    if (userId) {
      window.gtag?.('config', this.measurementId!, {
        user_id: userId,
      });
    }
  }
}

// Export singleton instance
export const analytics = new AnalyticsService();

// Export class for testing
export { AnalyticsService };
